import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type { Permission } from '@pm/shared';
import { config } from '@/config';
import { ApiError, api } from '@/lib/api';
import { keys } from '@/lib/query';

export interface Session {
  user: { id: string; email: string; name: string; locale: string };
  roles: string[];
  permissions: Permission[];
  workspaceRoles: Record<string, 'owner' | 'admin' | 'member' | 'viewer'>;
  managesTeam: boolean;
  /** Leads at least one department, which opens Departments without the permission. */
  leadsDepartment: boolean;
  /** The password was chosen by someone else and has to be replaced. */
  mustChangePassword: boolean;
}

/**
 * Signed out is an answer, not a failure: a refetch that fails keeps the
 * session it had, so a 401 has to replace it with null to actually sign out.
 */
export const fetchSession = () =>
  api.get<Session>('/auth/me').catch((error: unknown) => {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  });

/**
 * Asked again on a poll and whenever the tab regains focus: someone else can
 * change what this person may do - their role's grants, a workspace they were
 * added to - and a session kept for the life of the tab never showed it.
 */
export const useSession = () =>
  useQuery({
    queryKey: keys.session,
    queryFn: fetchSession,
    retry: false,
    // Only while signed in; the login page has nothing to poll for.
    refetchInterval: (query) => (query.state.data ? config.pollIntervalMs : false),
    refetchOnWindowFocus: true,
  });

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) =>
      api.post<Session['user']>('/auth/login', body),
    onSuccess: () => qc.invalidateQueries(),
  });
}

/**
 * Changing your own password. Everything is invalidated afterwards: the
 * pending-change flag is what the whole app is gated on, and the server has
 * dropped every other session.
 */
export function useChangePassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) =>
      api.post('/auth/password', body),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      // Navigate first, then drop the cache: clearing while the layout is
      // still mounted leaves it waiting on a session query that will 401.
      navigate('/login', { replace: true });
      qc.clear();
    },
  });
}
